'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getNights } from '@/app/components/hotel-detail/common/dates';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { sendMetricGaugeOnce, sendMetricIncrement, sendMetricTiming } from '@/app/components/hotel-detail/metrics';
import { PAGE_TYPE } from '@/app/common/pageType';
import { PROVIDERS } from '@/app/components/hotel-detail/common/consts';
import { URLParameters } from '@/app/common/urlParameters';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useSearchParams } from 'next/navigation';
import type { HotelDetailShownResponseCodes } from '@/app/common/starTrack/events/utils/starTrack.types.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import {
  hasSameExcludedChargesInAllTheOffers,
  includesPayAtHotel,
  isRoomsResponseError,
  setIndexOfRoomAndOptions,
  shouldShowDeferredPayments,
  shouldShowMyPriceBanner,
  shouldShowOneEuroDeferredPayments,
} from '@/app/components/hotel-detail/rooms/roomCommon';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { hasChildrenFreeOffer, isChildrenFreeConfig } from '@/app/components/hotel-detail/rooms/childrenFreeOffer';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider';
import { updateUrl } from '@/app/common/urlParametersUtils';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useCreateAsyncPricing } from './PricingProvider';
import { updateUrlTransportId, useUrlSearchParams } from './UrlSearchParamsProvider';
import { clientAppLog } from '../search/logging/clientAppLog';
/*
 * This provider is used to have values from offers that are needed in different components. It provides the offers
 * data information and a setter that must be used when the offers information is retrieved.
 */

export type OffersData = {
  // pricingId?: string; Getting it from the pricing SWR cache
  cheapestMealPlan?: MealPlanType;
  cheapestFilteredMealPlan?: MealPlanType;
  roomsIncludedInPriceForCheapestRoom?: number;
  roomsIncludedInPriceForCheapestFilteredRoom?: number;
  checkin?: string;
  checkout?: string;
  hasExpediaOffers?: boolean;
  internalHotelId?: number;
  // When searching for more than 1 room, the results may contain other offers with less rooms but
  // with capacity to accomodate all the travellers. In these cases a sorting is applied to show
  // first the offers with the number of rooms searched, then a message to indicate that the next
  // options have less rooms and last the the offers with less rooms.
  // `lessRoomsIndex` has the index of the first offer with less rooms to be able to inject the message
  // before this offer
  lessRoomsIndex?: number;
  nights?: number;
  noAvailableRooms?: boolean;
  noAvailableFlights?: boolean;
  mealPlansCount?: number;
  payAtHotelMealPlansCount?: number;
  priceType?: PriceType;
  showDeferredPayments?: boolean;
  showSmallAmountDeferredPayments?: boolean;
  hasFlashSaleOffers?: boolean;
  hasLoyaltyOffers?: boolean;
  showMyPriceBanner?: boolean;
  rooms?: RoomType[];
  roomsFiltered?: boolean;
  transport?: TransportEndpointResponse;
  transfer?: TransferResponse;
  responseCode?: HotelDetailShownResponseCodes;
  pricingId?: string;
  // MPH-1060: the vacation-catalog search the PRICING call actually used, straight from the rooms
  // response metadata. The URL's vcSearchId is only the one the page was entered with: pricing mints a
  // fresh search whenever it cannot recover that one, the rooms-error retry strips it, and APEX deeplinks
  // never carry it at all. This is the value HOTEL_DETAIL_SHOWN and CLICK2CHECKOUT report as vcSearchId,
  // so the events can be joined to the pricing search.
  pricingVcSearchId?: string;
  promotions?: PromotionSuccess | null;
  sameExcludedChargesInAllTheOffers?: boolean;
};

export type OffersContextType = {
  applyMealPlanFilter: boolean;
  isLoadingRooms: boolean;
  isSoldout: boolean;
  offersData?: OffersData;
  setApplyMealPlanFilter: Dispatch<SetStateAction<boolean>>;
  resetOffers: () => void;
  clearPreselectedTransport: () => void;
};

type OffersProviderProps = {
  children: ReactNode;
};

export const OffersContext = createContext<OffersContextType | null>(null);

export const offersDataEmpty: OffersData = {
  cheapestFilteredMealPlan: undefined,
  cheapestMealPlan: undefined,
  checkin: undefined,
  checkout: undefined,
  hasExpediaOffers: undefined,
  lessRoomsIndex: undefined,
  mealPlansCount: 0,
  nights: undefined,
  noAvailableFlights: undefined,
  noAvailableRooms: undefined,
  payAtHotelMealPlansCount: 0,
  priceType: undefined,
  pricingId: undefined,
  pricingVcSearchId: undefined,
  responseCode: undefined,
  rooms: undefined,
  roomsFiltered: undefined,
  roomsIncludedInPriceForCheapestFilteredRoom: undefined,
  roomsIncludedInPriceForCheapestRoom: undefined,
  showSmallAmountDeferredPayments: undefined,
  transport: undefined,
  transfer: undefined,
  hasFlashSaleOffers: false,
  showMyPriceBanner: false,
  showDeferredPayments: false,
  promotions: undefined,
  sameExcludedChargesInAllTheOffers: false,
};

const offersDataNotAvailable = { ...offersDataEmpty, noAvailableRooms: true };

type RetryStrategy = 'priceContext' | 'transportId' | 'internalIdTransport' | 'vcSearchId';
const RETRY_ORDER: readonly RetryStrategy[] = ['priceContext', 'transportId', 'internalIdTransport', 'vcSearchId'];

// Log flags derived from RETRY_ORDER so every rooms log reports every strategy (e.g. retryWithoutPriceContext=true).
const formatAttemptedRetries = (attempted: Set<RetryStrategy>) =>
  RETRY_ORDER.map(
    (strategy) => `retryWithout${strategy.charAt(0).toUpperCase()}${strategy.slice(1)}=${attempted.has(strategy)}`
  ).join(' ');

type SelectOffersFromRoomsDataParams = {
  roomsData: RoomsServiceResponseType;
  applyMealPlanFilter: boolean;
  freeCancellationFilter: boolean;
  mealPlanFilterValues: string[];
  totalRooms: number;
  nights: number | undefined;
  currency: string;
  internalHotelId: number | undefined;
  onlyChildrenFreeOffers: boolean;
};

type SelectOffersFromRoomsDataResult = {
  offersData: Partial<OffersData>;
  transportId: string | undefined;
};

export function selectOffersFromRoomsData({
  roomsData,
  applyMealPlanFilter,
  freeCancellationFilter,
  mealPlanFilterValues,
  totalRooms,
  nights: urlNights,
  currency,
  internalHotelId,
  onlyChildrenFreeOffers,
}: SelectOffersFromRoomsDataParams): SelectOffersFromRoomsDataResult {
  const { checkin, checkout } = roomsData.searchSummary;
  const nights = checkin && checkout ? getNights(checkin, checkout) : urlNights;
  const freeCancellationFilteredRooms =
    applyMealPlanFilter && freeCancellationFilter
      ? roomsData.selectedHotel.rooms.filter((room: RoomType) =>
          room.configurations.some((roomConfig: MealPlanType) => roomConfig.cancellationPolicy.type === 'REFUNDABLE')
        )
      : [...roomsData.selectedHotel.rooms];
  const roomsToFilter =
    freeCancellationFilteredRooms.length > 0 ? freeCancellationFilteredRooms : [...roomsData.selectedHotel.rooms];
  const mealPlanFilteredRooms =
    applyMealPlanFilter && mealPlanFilterValues.length
      ? roomsToFilter.filter((room: RoomType) =>
          room.configurations.some((roomConfig: MealPlanType) => mealPlanFilterValues.includes(roomConfig.boardClass))
        )
      : roomsToFilter;
  const filteredRooms = mealPlanFilteredRooms.length ? mealPlanFilteredRooms : roomsToFilter;
  // CUX 3966 (variant T): keep only the rates carrying the CHILDREN_FREE_OFFER promotion, dropping any
  // room left without a matching rate. Applied only to the rendered rooms chain; the "raw" fields below
  // still read from roomsData.selectedHotel.rooms. When no room survives the filter we short-circuit to
  // the existing "no available rooms" state (see below) instead of reaching sortedRooms[0].
  const childrenFreeFilteredRooms = onlyChildrenFreeOffers
    ? filteredRooms
        .map((room: RoomType) => ({
          ...room,
          // Compose with the results meal-plan filter: when the user arrived with a board-class filter
          // (mealPlanFilterValues — the same value SelectedMealPlanProvider preselects from), keep only
          // kids-free rates OF THAT board class. Without this, the kids-free filter would surface kids-free
          // rates of a board class the user had filtered out (e.g. Room Only when they picked Half Board).
          // No overlap => the room drops => existing sold-out state (below), never another meal plan.
          configurations: room.configurations.filter(
            (config: MealPlanType) =>
              isChildrenFreeConfig(config) &&
              (!mealPlanFilterValues.length || mealPlanFilterValues.includes(config.boardClass))
          ),
        }))
        .filter((room: RoomType) => room.configurations.length > 0)
    : filteredRooms;
  // MPH-553: with the badge gate this is now an EDGE case kept as a defensive anti-crash guard (avoids
  // sortedRooms[0] on an empty array). The badge in results guarantees at least one kids-free rate of the
  // filtered board class, so reaching here implies a memsearch/SHAPI inventory divergence, not the norm.
  if (onlyChildrenFreeOffers && childrenFreeFilteredRooms.length === 0) {
    return {
      offersData: {
        ...offersDataNotAvailable,
        internalHotelId,
        transport: roomsData.selectedTransport,
        transfer: roomsData.transfer,
        pricingId: roomsData.pricingId,
        pricingVcSearchId: roomsData.vcSearchId,
        responseCode: 'ROOM_NOT_AVAILABLE',
      },
      transportId: roomsData.selectedTransport?.transports[0]?.id,
    };
  }
  const sortedRooms: RoomType[] = setIndexOfRoomAndOptions(
    applyMealPlanFilter && (freeCancellationFilter || mealPlanFilterValues.length)
      ? childrenFreeFilteredRooms
          .map((room: RoomType) => {
            const selectedMealPlanIndex = room.configurations.findIndex(
              (roomConfig: MealPlanType) =>
                (!applyMealPlanFilter ||
                  !mealPlanFilterValues.length ||
                  mealPlanFilterValues.includes(roomConfig.boardClass)) &&
                (!freeCancellationFilter || roomConfig.cancellationPolicy.type === 'REFUNDABLE')
            );
            return { ...room, selectedMealPlanIndex: selectedMealPlanIndex === -1 ? 0 : selectedMealPlanIndex };
          })
          .sort(
            (a: RoomType, b: RoomType) =>
              a.configurations[a.selectedMealPlanIndex].finalPrice -
              b.configurations[b.selectedMealPlanIndex].finalPrice
          )
      : childrenFreeFilteredRooms
  );
  let lessRoomsIndex: number | undefined;
  if (totalRooms > 1) {
    sortedRooms.sort((a: RoomType, b: RoomType) => b.roomsIncludedInPrice - a.roomsIncludedInPrice);
    lessRoomsIndex = sortedRooms.findIndex((room: RoomType) => room.roomsIncludedInPrice < totalRooms);
  }
  const cheapestFilteredMealPlan =
    applyMealPlanFilter || freeCancellationFilter
      ? sortedRooms[0].configurations.find(
          (roomConfig: MealPlanType) =>
            (!applyMealPlanFilter ||
              !mealPlanFilterValues.length ||
              mealPlanFilterValues.includes(roomConfig.boardClass)) &&
            (!freeCancellationFilter || roomConfig.cancellationPolicy.type === 'REFUNDABLE')
        ) || sortedRooms[0].configurations[0]
      : sortedRooms[0].configurations[0];
  const transportId = roomsData.selectedTransport?.transports[0]?.id;
  const mealPlansCount = sortedRooms.reduce((acc, cur) => acc + cur.configurations.length, 0);
  return {
    offersData: {
      cheapestMealPlan: roomsData.selectedHotel.rooms[0].configurations[0],
      cheapestFilteredMealPlan,
      checkin,
      checkout,
      lessRoomsIndex,
      mealPlansCount,
      nights,
      noAvailableRooms: false,
      priceType: roomsData.priceType,
      roomsIncludedInPriceForCheapestRoom: roomsData.selectedHotel.rooms[0].roomsIncludedInPrice,
      roomsIncludedInPriceForCheapestFilteredRoom: sortedRooms[0].roomsIncludedInPrice,
      rooms: sortedRooms,
      // Compare against filteredRooms (user meal-plan/cancellation filters) and NOT sortedRooms, so the
      // CUX 3966 (variant T) children-free reduction does not trigger the "meal plan filter applied" banner.
      // In variant P sortedRooms derives from filteredRooms, so this is unchanged.
      roomsFiltered: roomsData.selectedHotel.rooms.length > filteredRooms.length,
      payAtHotelMealPlansCount: includesPayAtHotel(roomsData.selectedHotel.rooms),
      showDeferredPayments: shouldShowDeferredPayments(roomsData.selectedHotel.rooms),
      showMyPriceBanner: shouldShowMyPriceBanner(roomsData.selectedHotel.rooms),
      showSmallAmountDeferredPayments: shouldShowOneEuroDeferredPayments(roomsData.selectedHotel.rooms, currency),
      hasExpediaOffers: roomsData.selectedHotel.rooms.some((room: RoomType) =>
        room.configurations.some((roomConfig: MealPlanType) => roomConfig.supplier.id === PROVIDERS.Expedia.id)
      ),
      transport: roomsData.selectedTransport,
      transfer: roomsData.transfer,
      internalHotelId,
      responseCode: 'OK',
      pricingId: roomsData.pricingId,
      pricingVcSearchId: roomsData.vcSearchId,
      hasFlashSaleOffers: roomsData.anyFlashSales,
      hasLoyaltyOffers: roomsData.hasLoyaltyRate,
      promotions: roomsData.promotions,
      sameExcludedChargesInAllTheOffers: hasSameExcludedChargesInAllTheOffers(roomsData.selectedHotel.rooms),
    },
    transportId,
  };
}

export function OffersProvider(props: OffersProviderProps) {
  const { children } = props;
  const [offersData, setOffersData] = useState<OffersData>(offersDataEmpty);
  const {
    isLoading: isLoadingPricingId,
    isValidating: isValidatingPricingId,
    data: asyncPricingData,
    error: pricingError,
    mutate: mutatePricing,
  } = useCreateAsyncPricing();
  const initialParams = useInitialParams();
  const urlSearchParams = useUrlSearchParams();
  const searchParams = useSearchParams();
  const pageTypeNavigation = searchParams.get(URLParameters.PAGE_TYPE);
  const initialTime = useRef(Date.now());
  const { hdpSearchId, pageType } = initialParams;
  const { localization } = usePageWrapperData();
  const { data: roomsData, error } = useFetchRooms();
  const { handleChangeStay } = useCommonClient();
  const searchPageData = useSearchPageData();
  const onlyChildrenFreeOffers = localization.configValuesApp?.onlyChildrenFreeOffers ?? false;
  // CUX 3966 (MPH-553): the kids-free filter is gated by the results→detail badge signal. Capture the
  // fromKidsFreeBadge flag from the URL (read from window.location via getSearchParams, so it also works for
  // the pushState-based SPA navigation, which does not re-emit useSearchParams). Keyed on internalHotelId so
  // that when OffersProvider survives a similar-hotel detail→detail navigation it re-reads the flag for the
  // new hotel (whose URL does not carry it => false) instead of inheriting the previous hotel's value. The
  // capture runs before the strip effect below. Reload / shared URL carry no flag => cameFromKidsFreeBadge
  // stays false => no filtering (intended).
  const cameFromKidsFreeBadgeRef = useRef<{ hotelId: number; value: boolean } | null>(null);
  if (cameFromKidsFreeBadgeRef.current?.hotelId !== initialParams.internalHotelId) {
    cameFromKidsFreeBadgeRef.current = {
      hotelId: initialParams.internalHotelId,
      value: getSearchParams()[URLParameters.FROM_KIDS_FREE_BADGE] === 'true',
    };
  }
  const cameFromKidsFreeBadge = cameFromKidsFreeBadgeRef.current?.value ?? false;
  // Effective filter condition: variant T (config flag) AND the origin card actually showed the kids-free badge.
  const applyChildrenFreeFilter = onlyChildrenFreeOffers && cameFromKidsFreeBadge;
  // In evo a meal-plan URL filter does not hard-filter the rooms — it only preselects the matching
  // room/meal plan (see SelectedMealPlanProvider). So start with filtering off so every room and meal
  // plan stays available and the "meal plan filter applied" message never shows. Legacy keeps filtering.
  const [applyMealPlanFilter, setApplyMealPlanFilter] = useState<boolean>(
    !localization.configValuesApp?.evolutionEnabled
  );
  const attemptedRetries = useRef<Set<RetryStrategy>>(new Set());
  // CUX 3966: dedupe the kids-stay-free metrics + INFO log to once per hotel. The [roomsData] effect
  // re-runs on every rooms refetch (e.g. changing the flight), which would otherwise re-emit the beacons
  // and inflate the board's counts/distributions. Keyed on internalHotelId, so navigating to another
  // hotel re-emits.
  const kidsStayFreeReportedHotelId = useRef<number | undefined>(undefined);
  const [retryNonce, setRetryNonce] = useState(0);
  const pricingFallbackAttempted = useRef(false);

  const transportIsSoldOut =
    !urlSearchParams.accommodationOnly && offersData?.transport ? offersData.transport.transports.length === 0 : false;
  const isSoldout = urlSearchParams.isPastCheckin || offersData?.noAvailableRooms || transportIsSoldOut;

  // Takes into account when back/forward navigation is loading a different hotel (when using similar hotels section)
  const isLoadingDifferentHotel = offersData?.internalHotelId !== initialParams.internalHotelId;

  // Takes into account when the transport is changed. Transport is set to null when changing the transport, then rooms endpoint is called
  // and the transport is set again when the response is received.
  const isLoadingRooms =
    !isSoldout &&
    (isLoadingPricingId ||
      asyncPricingData?.pricingId !== offersData.pricingId ||
      isLoadingDifferentHotel ||
      (!urlSearchParams.accommodationOnly && !offersData?.transport?.transports[0]?.id));

  // CUX 3966 (MPH-553): strip the one-shot badge flag from the URL once on mount (replaceState via
  // updateUrl, no reload / no router resync), so sharing or reloading the detail URL does not re-trigger the
  // filter. The value was already captured in cameFromKidsFreeBadgeRef above, before this strip runs.
  useEffect(() => {
    if (cameFromKidsFreeBadgeRef.current?.value) {
      updateUrl({ queryParamsToRemove: [URLParameters.FROM_KIDS_FREE_BADGE] });
    }
  }, []);

  // pricing-id can "fail successfully": the route replies HTTP 200 with { errorType } and no pricingId
  // when the engine can't honour the request (see api/hotel-detail/pricing-id/route.ts). This is most
  // reachable from apex deep links, whose rctx/rsign target price (resolveRatesCollection) can drift past
  // what is bookable. SWR sees a 200 (no `error`), useFetchRooms never keys (no pricingId), and the page
  // would load forever while HOTEL_DETAIL_SHOWN never fires. Treat "settled with no pricingId" (or a hard
  // SWR error) as a terminal pricing failure. Guarded by isValidating so an in-flight retry isn't counted.
  // MPH-785: the failure must be the ABSENCE of a usable pricingId, not merely the presence of an
  // error. SWR keeps the previous data when a revalidation throws, and since this MR the page also
  // re-prices on tab-wake and on review entry — moments when a request can fail for a second while a
  // perfectly good pricingId is still cached. Tripping on the error alone sent those cases through the
  // two-strike fallback below and latched the page as sold out (ejecting the review user), with no way
  // back: revalidateOnReconnect is off and SWR's poller stops once an error is cached.
  const pricingFailed =
    !isSoldout &&
    !isValidatingPricingId &&
    (Boolean(pricingError) || Boolean(asyncPricingData)) &&
    !asyncPricingData?.pricingId;

  // The vcSearchId / internalIdTransport retries remove a param from the URL via updateUrl(), which uses
  // window.history.replaceState and intentionally does NOT sync the Next.js router (SLIDERS-2188), so
  // useSearchParams() never re-emits. We therefore drive the retry refetch with local state: bumping
  // retryNonce re-renders this provider, useCreateAsyncPricing() rebuilds its fetcher from the now-updated
  // window.location (param gone), and mutatePricing() fires that fresh fetcher. vcSearchId is excluded from
  // the pricing SWR key, so this explicit mutate is what forces its refetch (internalIdTransport is in the
  // key, so the re-render alone already refetches it).
  useEffect(() => {
    if (retryNonce > 0) {
      mutatePricing();
    }
  }, [retryNonce]);

  // Re-price the same request as plain pricing: drop the apex rctx/rsign/rates target price (which the
  // engine may no longer honour) and force the refetch through retryNonce, since the SWR key is unchanged.
  // The pricing fetcher already drops the context after a successful call; the reset here covers a hard
  // pricing error, where that never ran. Shared by the pricing-failed and rooms-error retries. (MPH-1140)
  const retryWithoutPriceContext = () => {
    initialParams.resetPriceContext?.();
    setRetryNonce((nonce) => nonce + 1);
  };

  // Re-picking the stay already on screen (date picker, price calendar, carousel...) runs resetOffers() but
  // changes no pricing/rooms key, so nothing would refetch and the page would load forever. Force a fresh
  // pricing for that same stay instead; a real date change already refetches through the key. (MPH-1140)
  const lastPickedStay = useRef({ checkin: urlSearchParams.checkin, checkout: urlSearchParams.checkout });
  useEffect(() => {
    const { checkin, checkout, datesSetCount } = urlSearchParams;
    const sameStay = lastPickedStay.current.checkin === checkin && lastPickedStay.current.checkout === checkout;
    lastPickedStay.current = { checkin, checkout };
    if (datesSetCount > 0 && sameStay) {
      mutatePricing();
    }
  }, [urlSearchParams.datesSetCount]);

  // Allow a fresh fallback attempt whenever the hotel changes or a valid pricingId arrives, so navigating
  // between hotels / changing dates isn't stuck with a consumed retry. A failing retry keeps pricingId
  // undefined, so this never resets mid-retry (no loop).
  useEffect(() => {
    pricingFallbackAttempted.current = false;
  }, [initialParams.internalHotelId, asyncPricingData?.pricingId]);

  useEffect(() => {
    if (!pricingFailed) return;
    // First failure: retry pricing once as plain pricing, with no target-price constraint (which the
    // engine can always satisfy).
    if (!pricingFallbackAttempted.current) {
      pricingFallbackAttempted.current = true;
      clientAppLog({
        logMessage: `pricing-id: settled without pricingId, retrying without apex price context - hdpSearchId=${hdpSearchId} - error=${pricingError ?? ''} - response=${JSON.stringify(asyncPricingData ?? {})}`,
        codeSourceName: 'OffersProvider',
        level: 'WARN',
        url: window?.location?.href,
      });
      retryWithoutPriceContext();
      return;
    }
    // Retry also failed: surface a terminal responseCode so the page stops loading and HOTEL_DETAIL_SHOWN
    // fires instead of wedging silently. Use a dedicated code so these can be told apart in analytics:
    // ERROR_PRICING_RESPONSE_INVALID for the route's success-shaped failure (200 + errorType, no pricingId),
    // ERROR_PRICING_ASYNC for a hard SWR/network error on the pricing call.
    const responseCode = pricingError ? 'ERROR_PRICING_ASYNC' : 'ERROR_PRICING_RESPONSE_INVALID';
    clientAppLog({
      logMessage: `pricing-id: no pricingId after fallback retry, giving up (${responseCode}) - hdpSearchId=${hdpSearchId} - error=${pricingError ?? ''} - response=${JSON.stringify(asyncPricingData ?? {})}`,
      codeSourceName: 'OffersProvider',
      level: 'ERROR',
      url: window?.location?.href,
    });
    setOffersData((previous) => ({ ...previous, ...offersDataNotAvailable, responseCode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingFailed]);

  // The transport id needs to be updated in the URL when the transport is changed, the change is done in the review step, if the user goes
  // back to the flights step, the transport id needs to be updated in the URL again, because it was the previous transport id.
  useEffect(() => {
    if (offersData && !urlSearchParams.accommodationOnly && offersData?.transport) {
      const transportId = offersData.transport.transports[0]?.id;
      if (transportId && transportId !== searchParams.get(URLParameters.TRANSPORT_ID)) {
        updateUrlTransportId(transportId);
      }
    }
  }, [offersData, urlSearchParams.accommodationOnly, searchParams]);

  useEffect(() => {
    if (!error) {
      return;
    }
    setOffersData((previous) => ({ ...previous, ...offersDataNotAvailable, responseCode: 'ERROR_FETCHING_ROOMS' }));
    clientAppLog({
      logMessage: `rooms: response api error=${error} hdpSearchId=${hdpSearchId}`,
      codeSourceName: 'OffersProvider',
      level: 'WARN',
      url: window?.location?.href,
    });
  }, [error]);

  useEffect(() => {
    try {
      if (!roomsData) {
        return;
      }
      if (isRoomsResponseError(roomsData)) {
        const roomsErrors: readonly string[] = roomsData.errors ?? [];
        const retryStrategies: Record<RetryStrategy, { applies: () => boolean; attempt: () => void }> = {
          priceContext: {
            // The apex rate context pins the pricing to a target price. When that rate is gone the engine
            // can still hand out a pricingId whose rooms call fails with PROPOSAL_NOT_FOUND, so retry as
            // plain pricing before declaring the stay sold out. Limited to that error: dropping the price
            // cannot fix any other one, so a genuinely sold-out stay isn't delayed by a useless round trip.
            // First in line: it keeps the rest of the request (transport included). (MPH-1140)
            applies: () => Boolean(asyncPricingData?.withPriceContext) && roomsErrors.includes('PROPOSAL_NOT_FOUND'),
            attempt: retryWithoutPriceContext,
          },
          transportId: {
            applies: () => Boolean(initialParams.transportId),
            // Remove transportID from url and force a new pricing to check results with a different transport
            attempt: () => initialParams.resetTransportId?.(),
          },
          internalIdTransport: {
            applies: () => Boolean(searchParams.get(URLParameters.INTERNAL_ID_TRANSPORT)),
            attempt: () => {
              updateUrl({ queryParamsToRemove: [URLParameters.INTERNAL_ID_TRANSPORT] });
              setRetryNonce((nonce) => nonce + 1);
            },
          },
          vcSearchId: {
            applies: () => Boolean(searchParams.get(URLParameters.VC_SEARCH_ID)),
            // updateUrl() uses replaceState without syncing the Next router (SLIDERS-2188), so removing the
            // param neither re-emits useSearchParams nor changes the pricing SWR key. Bump retryNonce to
            // re-render and force a fresh pricing fetch (without searchId) via the retryNonce effect.
            attempt: () => {
              updateUrl({ queryParamsToRemove: [URLParameters.VC_SEARCH_ID] });
              setRetryNonce((nonce) => nonce + 1);
            },
          },
        };
        const nextStrategy = RETRY_ORDER.find(
          (strategy) => !attemptedRetries.current.has(strategy) && retryStrategies[strategy].applies()
        );
        if (nextStrategy) {
          attemptedRetries.current.add(nextStrategy);
          retryStrategies[nextStrategy].attempt();
        } else if (
          (roomsData as RoomsServiceResponseErrorType).errors?.[0] === 'SEARCH_PARAMS_DATES' &&
          pageTypeNavigation === PAGE_TYPE.REVIEW
        ) {
          handleChangeStay();
        } else {
          clientAppLog({
            logMessage: `rooms: response api (room) error=${JSON.stringify(roomsData)} ${formatAttemptedRetries(attemptedRetries.current)}`,
            codeSourceName: 'OffersProvider',
            level: 'WARN',
            url: window?.location?.href,
          });
          setOffersData((previous) => ({
            ...previous,
            ...offersDataNotAvailable,
            // Attribute the sold-out to the hotel whose rooms response this is:
            // useReportHotelNoAvailability only reports a ROOM_NOT_AVAILABLE that carries the
            // current hotel's id (offersData survives similar-hotel detail->detail navigation).
            internalHotelId: initialParams.internalHotelId,
            responseCode: 'ROOM_NOT_AVAILABLE',
          }));
        }
        return;
      }
      // Now roomsData is RoomsServiceResponseType
      const typedRoomsData = roomsData as RoomsServiceResponseType;
      if (
        !urlSearchParams.accommodationOnly &&
        (!typedRoomsData.selectedTransport || typedRoomsData.selectedTransport.transports?.length === 0)
      ) {
        clientAppLog({
          logMessage: `rooms: response api (transport) error=${JSON.stringify(roomsData)} ${formatAttemptedRetries(attemptedRetries.current)}`,
          codeSourceName: 'OffersProvider',
          level: 'WARN',
          url: window?.location?.href,
        });
        setOffersData((previous) => ({
          ...previous,
          pricingId: typedRoomsData.pricingId,
          pricingVcSearchId: typedRoomsData.vcSearchId,
          transport: { transports: [] },
          noAvailableFlights: true,
          responseCode: 'TRANSPORT_NOT_AVAILABLE',
        }));
        return;
      }
      if (typedRoomsData.changedTransport) {
        // changedTransport is set to true when changing the flight to know when we are mutating the data and
        // asking rooms endpoint again for fresh data. We ignore this change and wait for the refreshed data.
        return;
      }

      if (attemptedRetries.current.size > 0) {
        clientAppLog({
          logMessage: `rooms: response api OK - ${formatAttemptedRetries(attemptedRetries.current)}`,
          codeSourceName: 'OffersProvider',
          level: 'WARN',
          url: window?.location?.href,
        });
        attemptedRetries.current.clear();
      }

      const { offersData: newOffersData, transportId } = selectOffersFromRoomsData({
        roomsData: typedRoomsData,
        applyMealPlanFilter,
        freeCancellationFilter: urlSearchParams.freeCancellationFilter,
        mealPlanFilterValues: urlSearchParams.mealPlanFilterValues,
        totalRooms: urlSearchParams.totalRooms,
        nights: urlSearchParams.nights,
        currency: localization.currency,
        internalHotelId: initialParams.internalHotelId,
        onlyChildrenFreeOffers: applyChildrenFreeFilter,
      });
      if (transportId) {
        updateUrlTransportId(transportId);
      }
      setOffersData((previous) => ({ ...previous, ...newOffersData }));

      // CUX 3966 observability (MPH-553): emit the kids-stay-free metrics and INFO log only for hotels the
      // user reached from a results card showing the kids-free badge (cameFromKidsFreeBadge), in BOTH
      // variants — scope "general" for P (no filter, real detail price) and "test-t" for T (filter applied).
      // Deduped per hotel so a rooms refetch (e.g. flight change) does not re-emit and inflate the board.
      const alreadyReported =
        initialParams.internalHotelId !== undefined &&
        kidsStayFreeReportedHotelId.current === initialParams.internalHotelId;
      if (cameFromKidsFreeBadge && !alreadyReported) {
        // Compare the results-page price with the detail "from" price (cheapest visible rate after filtering)
        // to flag a price change. Both sides use the ABSOLUTE TOTAL (price.total.price and finalTotalPrice are
        // always totals regardless of priceType/isPerPerson), avoiding the per-person-vs-total mismatch (e.g.
        // HO results show a total). Caveat: searchPageData reflects the last results search, so a different
        // date/occupancy on detail can still differ.
        const resultsPrice = searchPageData?.productsData?.dealCards?.find(
          (card) => card.locales?.hotelId === initialParams.internalHotelId
        )?.product?.rate?.price?.total?.price;
        const detailPrice = newOffersData.cheapestFilteredMealPlan?.finalTotalPrice;
        const pricesDiffer =
          resultsPrice !== undefined &&
          detailPrice !== undefined &&
          Math.round(resultsPrice) !== Math.round(detailPrice);

        // Measure the impact of removing the non-kids-free rates over the RAW inventory
        // (roomsData.selectedHotel.rooms), independent of the actual UI filter: hypothetical in P, real in T,
        // same numbers in both. A meal plan (boardClass) counts as removed only when NO room keeps a kids-free
        // rate of that board class, i.e. total distinct board classes minus distinct kids-free board classes.
        const rawRooms = typedRoomsData.selectedHotel.rooms;
        const offersTotal = rawRooms.reduce((acc, room) => acc + room.configurations.length, 0);
        const offersRemoved = rawRooms.reduce(
          (acc, room) => acc + room.configurations.filter((config) => !isChildrenFreeConfig(config)).length,
          0
        );
        const roomsTotal = rawRooms.length;
        const roomsRemoved = rawRooms.filter((room) => !hasChildrenFreeOffer(room.configurations)).length;
        const mealplansTotal = new Set(
          rawRooms.flatMap((room) => room.configurations.map((config) => config.boardClass))
        ).size;
        const kidsFreeMealplans = new Set(
          rawRooms.flatMap((room) =>
            room.configurations.filter(isChildrenFreeConfig).map((config) => config.boardClass)
          )
        ).size;
        const mealplansRemoved = mealplansTotal - kidsFreeMealplans;

        const variant = onlyChildrenFreeOffers ? 'T' : 'P';
        const scope = onlyChildrenFreeOffers ? 'test-t' : 'general';
        const base = `app.kids-stay-free.${pageType}.${scope}`;
        sendMetricIncrement(`${base}.hotels`);
        sendMetricTiming(`${base}.offers-total`, offersTotal);
        sendMetricTiming(`${base}.offers-removed`, offersRemoved);
        sendMetricTiming(`${base}.rooms-total`, roomsTotal);
        sendMetricTiming(`${base}.rooms-removed`, roomsRemoved);
        sendMetricTiming(`${base}.mealplans-total`, mealplansTotal);
        sendMetricTiming(`${base}.mealplans-removed`, mealplansRemoved);
        // Per-hotel removal percentage as its own timer (0-100 by construction). The board reads these
        // directly (mean/median/p90) instead of dividing two separately-beaconed sums: total and removed are
        // delivered as independent sendBeacon calls, so they can land in different flush buckets and a
        // ratio-of-sums can exceed 100% (impossible per hotel). A per-hotel percentage never can.
        if (offersTotal > 0) {
          sendMetricTiming(`${base}.offers-removed-pct`, Math.round((offersRemoved / offersTotal) * 100));
        }
        if (roomsTotal > 0) {
          sendMetricTiming(`${base}.rooms-removed-pct`, Math.round((roomsRemoved / roomsTotal) * 100));
        }
        if (mealplansTotal > 0) {
          sendMetricTiming(`${base}.mealplans-removed-pct`, Math.round((mealplansRemoved / mealplansTotal) * 100));
        }
        if (pricesDiffer) {
          sendMetricIncrement(`${base}.price-changed`);
        }
        // Classify the detail-page composition (the results card had the kids-free badge). Mutually exclusive
        // counters, so the board can show the share of each case: none (all offers removed / no kids-free on
        // detail), all (every offer is kids-free), mixed (some of each). Guarded by offersTotal > 0 so a
        // no-availability detail (no offers at all) does not masquerade as "none" and pollute the critical WARN.
        if (offersTotal > 0) {
          const offersKidsFree = offersTotal - offersRemoved;
          const detailCategory = offersKidsFree === 0 ? 'none' : offersKidsFree === offersTotal ? 'all' : 'mixed';
          sendMetricIncrement(`${base}.detail-${detailCategory}`);

          const logBody =
            `variant=${variant} category=${detailCategory} ` +
            `offersTotal=${offersTotal} offersRemoved=${offersRemoved} ` +
            `roomsTotal=${roomsTotal} roomsRemoved=${roomsRemoved} ` +
            `mealplansTotal=${mealplansTotal} mealplansRemoved=${mealplansRemoved} ` +
            `resultsPrice=${resultsPrice ?? 'NA'} detailPrice=${detailPrice ?? 'NA'} pricesDiffer=${pricesDiffer} ` +
            `hdpSearchId=${hdpSearchId} detailUrl=${window?.location?.href} resultsUrl=${document.referrer}`;
          if (detailCategory === 'none') {
            // Critical case: the results badge promised kids-free but the detail has ZERO kids-free offers, so
            // variant T removes every offer. Always logged (never gated) at WARN so it stands out and is
            // traceable/alertable, with both the detail and origin results URLs.
            clientAppLog({
              logMessage: `kids-free-all-removed ${logBody}`,
              codeSourceName: 'OffersProvider',
              level: 'WARN',
              url: window?.location?.href,
            });
          } else if (localization.configValuesApp?.kidsStayFreeOffersLog) {
            // mixed / all-kids-free cases — gated behind the kids_stay_free_offers_log CMS toggle (off by default).
            clientAppLog({
              logMessage: `kids-stay-free-test ${logBody}`,
              codeSourceName: 'OffersProvider',
              level: 'INFO',
              url: window?.location?.href,
            });
          }
        }

        // Mark this hotel as reported so a rooms refetch (e.g. flight change) does not re-emit.
        if (initialParams.internalHotelId !== undefined) {
          kidsStayFreeReportedHotelId.current = initialParams.internalHotelId;
        }
      }

      const renderTime = Date.now() - initialTime.current;
      // rooms-render metric it is used to know first rooms load. It won't be sent when session renewed or
      // new rooms loaded after changing the flight
      sendMetricGaugeOnce(`app.duration.${pageType}.rooms-render`, renderTime);
    } catch (roomsError) {
      clientAppLog({
        logMessage: `rooms: processing response error - hdpSearchId=${hdpSearchId} - error:${roomsError}`,
        codeSourceName: 'OffersProvider',
        level: 'ERROR',
        url: window?.location?.href,
      });
      setOffersData((previous) => ({
        ...previous,
        ...offersDataNotAvailable,
        // MPH-1060: the response itself was valid and carries the pricing search id — only our processing
        // of it threw — so keep the id rather than letting the events fall back to the URL value.
        ...(roomsData && !isRoomsResponseError(roomsData)
          ? { pricingVcSearchId: (roomsData as RoomsServiceResponseType).vcSearchId }
          : {}),
        responseCode: 'ERROR_PROCESSING_ROOMS_RESPONSE',
      }));
    }
  }, [roomsData]);

  useEffect(() => {
    if (applyMealPlanFilter || !roomsData || isRoomsResponseError(roomsData)) return;
    const { offersData: newOffersData } = selectOffersFromRoomsData({
      roomsData: roomsData as RoomsServiceResponseType,
      applyMealPlanFilter: false,
      freeCancellationFilter: urlSearchParams.freeCancellationFilter,
      mealPlanFilterValues: urlSearchParams.mealPlanFilterValues,
      totalRooms: urlSearchParams.totalRooms,
      nights: urlSearchParams.nights,
      currency: localization.currency,
      internalHotelId: initialParams.internalHotelId,
      onlyChildrenFreeOffers: applyChildrenFreeFilter,
    });
    setOffersData((previous) => ({ ...previous, ...newOffersData }));
  }, [applyMealPlanFilter]);

  const resetOffers = useCallback(() => {
    // A reset starts a fresh search attempt (new dates/travellers): restore the full retry budget
    // so the new stay doesn't inherit strategies consumed by the previous one and reach
    // ROOM_NOT_AVAILABLE / a terminal pricing error without its own retries.
    attemptedRetries.current.clear();
    pricingFallbackAttempted.current = false;
    setOffersData(offersDataEmpty);
  }, []);

  const clearPreselectedTransport = useCallback(() => {
    setOffersData((prev) => ({ ...prev, transport: undefined }));
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isLoadingRooms,
      isSoldout,
      offersData: {
        ...offersData,
      },
      resetOffers,
      clearPreselectedTransport,
      setApplyMealPlanFilter,
      applyMealPlanFilter,
    }),
    [
      offersData,
      isLoadingRooms,
      isSoldout,
      resetOffers,
      clearPreselectedTransport,
      setApplyMealPlanFilter,
      applyMealPlanFilter,
    ]
  );

  return <OffersContext.Provider value={memoizedValue}>{children}</OffersContext.Provider>;
}

export function useOffersContext(): OffersContextType {
  const context = useContext(OffersContext);

  if (context === null) {
    throw new Error('useOffersContext must be within a OffersContext');
  }
  return context;
}
